// ShareDialog — modal genérico de compartilhamento. Usado por gravações,
// cadernos (e futuramente bancos). Multi-email com chip input, mensagem
// opcional, anti-enumeration (cliente NUNCA vê quais emails existem).

import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import { CheckCircle2, Loader2, Share2, X } from "lucide-react";
import { api } from "./api";

interface ShareDialogState {
  open: boolean;
  kind: "lecture" | "caderno" | "bank" | "nota";
  resourceId: string;
  resourceTitle: string;
}

export const shareDialogState = signal<ShareDialogState | null>(null);

export function openShareDialog(opts: {
  kind: "lecture" | "caderno" | "bank" | "nota";
  resourceId: string;
  resourceTitle: string;
}) {
  shareDialogState.value = { open: true, ...opts };
}

export function ShareDialog() {
  const state = shareDialogState.value;
  if (!state?.open) return null;

  return <DialogBody {...state} />;
}

function DialogBody({
  kind,
  resourceId,
  resourceTitle
}: {
  kind: "lecture" | "caderno" | "bank" | "nota";
  resourceId: string;
  resourceTitle: string;
}) {
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState<"idle" | "sending" | "done" | "error">(
    "idle"
  );
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [sentCount, setSentCount] = useState(0);

  function close() {
    shareDialogState.value = null;
  }

  function addCurrent() {
    const e = draft.trim().toLowerCase();
    if (!e) return;
    if (!e.includes("@") || !e.includes(".")) {
      setErrMsg("Email inválido.");
      return;
    }
    if (emails.includes(e)) {
      setDraft("");
      return;
    }
    setEmails([...emails, e]);
    setDraft("");
    setErrMsg(null);
  }

  function remove(e: string) {
    setEmails(emails.filter((x) => x !== e));
  }

  async function send() {
    if (emails.length === 0) {
      // permite enviar o que está no draft direto
      if (draft.trim()) {
        addCurrent();
      }
      if (emails.length === 0) {
        setErrMsg("Adicione ao menos 1 email.");
        return;
      }
    }
    setPhase("sending");
    setErrMsg(null);
    try {
      const r = await api.createShare({
        kind,
        resourceId,
        recipientEmails: emails,
        message: message.trim() || undefined
      });
      setSentCount(r.count);
      setPhase("done");
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  const kindLabel =
    kind === "lecture"
      ? "gravação"
      : kind === "caderno"
        ? "caderno"
        : kind === "nota"
          ? "anotação"
          : "banco";

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-2xl">
        <header class="mb-4 flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Share2 className="size-4 text-violet-600" />
              Compartilhar {kindLabel}
            </h2>
            <p class="mt-0.5 truncate text-xs text-muted-foreground">
              {resourceTitle}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            title="Fechar"
            aria-label="Fechar modal de compartilhamento"
            class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        {phase === "done" ? (
          <div class="space-y-3 text-center">
            <CheckCircle2 className="mx-auto size-10 text-emerald-600" />
            <p class="text-sm">
              Convite enviado a <strong>{sentCount}</strong>{" "}
              {sentCount === 1 ? "destinatário" : "destinatários"}.
            </p>
            <p class="text-[12px] text-muted-foreground">
              Cada destinatário verá um convite na bandeja de compartilhamentos
              e poderá aceitar ou recusar individualmente. Por privacidade, não
              indicamos quem já tem cadastro ou quem ainda vai se cadastrar.
            </p>
            <button
              type="button"
              onClick={close}
              class="rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Emails (até 20)
            </label>
            <div class="rounded-md border border-border bg-background p-2">
              {emails.length > 0 && (
                <div class="mb-1 flex flex-wrap gap-1">
                  {emails.map((e) => (
                    <span
                      key={e}
                      class="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-xs text-violet-700 dark:text-violet-300"
                    >
                      {e}
                      <button
                        type="button"
                        onClick={() => remove(e)}
                        class="text-violet-600 hover:text-violet-800 dark:text-violet-300"
                        title="Remover"
                        aria-label={`Remover ${e}`}
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="email"
                value={draft}
                onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" ||
                    e.key === "," ||
                    e.key === " "
                  ) {
                    e.preventDefault();
                    addCurrent();
                  }
                }}
                onBlur={addCurrent}
                placeholder={
                  emails.length === 0
                    ? "fulano@email.com (Enter pra adicionar)"
                    : "adicionar outro…"
                }
                class="w-full bg-transparent px-1 py-1 text-sm outline-none"
              />
            </div>
            <p class="mt-1 text-[11px] text-muted-foreground">
              Aperte Enter, vírgula ou espaço pra adicionar cada email.
            </p>

            <label class="mt-3 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Mensagem (opcional)
            </label>
            <textarea
              value={message}
              onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
              rows={2}
              maxLength={500}
              placeholder="Ex: 'Te separei esse material que pode ajudar pra prova de quarta.'"
              class="mt-1 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
            />

            {errMsg && (
              <p class="mt-2 text-sm text-destructive">{errMsg}</p>
            )}

            <div class="mt-4 rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[11px] text-blue-700 dark:text-blue-300">
              Cada destinatário começa do zero: progresso, anotações e
              conversas com a IA são <strong>independentes</strong> das suas.
            </div>

            <div class="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                class="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={send}
                disabled={phase === "sending"}
                class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {phase === "sending" ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Share2 className="size-4" />
                )}
                Enviar convite
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
